<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center"> Talent Releases</v-card-title>
    <v-card-text>
      <v-list>
        <v-list-item v-for="talentRelease of talentReleases">
          <TalentReleaseComponent
            :castCrew="talentRelease"
            :submission="registration"
            :releaseType="talentType" />
        </v-list-item>
      </v-list>
    </v-card-text>
  </v-card>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      Location Release
      <div class="d-flex justify-end mb-2">
        <v-icon color="primary" @click="addRelease('location')">mdi-plus</v-icon>
      </div>
    </v-card-title>
    <v-card-text>
      <v-list>
        <v-list-item v-for="locationRelease of locationReleases">
          <ReleaseComponent
            :release="locationRelease"
            :submission="registration"
            :releaseType="locationType" />
        </v-list-item>
      </v-list>
    </v-card-text>
  </v-card>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      Music Releases
      <div class="d-flex justify-end mb-2">
        <v-icon color="primary" @click="addRelease('music')">mdi-plus</v-icon>
      </div>
    </v-card-title>
    <v-card-text>
      <v-list>
        <v-list-item v-for="musicRelease of musicReleases">
          <ReleaseComponent
            :release="musicRelease"
            :submission="registration"
            :releaseType="musicType" />
        </v-list-item>
      </v-list>
    </v-card-text>
  </v-card>
</template>
<script setup lang="ts">
import ReleaseComponent from '@/components/release/ReleaseComponent.vue';
import TalentReleaseComponent from '@/components/release/TalentReleaseComponent.vue';
import {
  IReleaseDTO,
  ISubmissionCastCrewDTO,
  ITeamDTO,
  ReleaseDTO,
  ReleaseTypeDTO,
  ReleaseTypeEnum,
  SubmissionCastCrewDTO,
  SubmissionDTO,
} from '@/dtos';
import { useReleaseStore } from '@/stores';
import { useAdminRegistrationStore } from '@/stores/modules/admin';
import { useCastCrewStore } from '@/stores/modules/castcrew';
import { computed, ref, watch } from 'vue';

interface Props {
  teamId: string;
  yearId: string;
  team: ITeamDTO;
}

const emit = defineEmits(['releaseStatus', 'snack']);
const props = defineProps<Props>();
const teamId = ref<string>(props.teamId);
const yearId = ref<string>(props.yearId);

const registrationStore = useAdminRegistrationStore();
const { data: registrationData } = registrationStore.getTeamRegistration(yearId, teamId);
const releaseStore = useReleaseStore();
const castCrewStore = useCastCrewStore();

const registration = computed(() => registrationData.value || new SubmissionDTO());
const submissionId = computed(() => registration.value.id || '');
const { data: castCrewData } = castCrewStore.getCastAndCrew(submissionId);

const { data: releaseTypesData } = releaseStore.getReleaseTypes();
const { data: releases } = releaseStore.getReleasesForSubmission(submissionId.value);

const locationType = computed(() => {
  return (
    releaseTypesData.value?.data.find((type) => type.id == ReleaseTypeEnum.Location.id) ||
    new ReleaseTypeDTO()
  );
});
const musicType = computed(() => {
  return (
    releaseTypesData.value?.data.find((type) => type.id == ReleaseTypeEnum.Music.id) ||
    new ReleaseTypeDTO()
  );
});
const talentType = computed(() => {
  return (
    releaseTypesData.value?.data.find((type) => type.id == ReleaseTypeEnum.Talent.id) ||
    new ReleaseTypeDTO()
  );
});

const addRelease = (type: 'location' | 'music' | 'talent') => {
  if (type === 'location') {
    locationReleases.value.push(new ReleaseDTO());
  } else if (type === 'music') {
    musicReleases.value.push(new ReleaseDTO());
  } else if (type === 'talent') {
    talentReleases.value.push(new SubmissionCastCrewDTO());
  }
};

const locationReleases = ref<IReleaseDTO[]>([]);
const musicReleases = ref<IReleaseDTO[]>([]);
const talentReleases = ref<ISubmissionCastCrewDTO[]>([]);

watch(
  () => [releases.value, castCrewData.value],
  () => {
    locationReleases.value =
      releases.value?.data.filter(
        (release) => release.releaseType?.id == ReleaseTypeEnum.Location.id,
      ) || [];
    musicReleases.value =
      releases.value?.data.filter(
        (release) => release.releaseType?.id == ReleaseTypeEnum.Music.id,
      ) || [];

    talentReleases.value =
      castCrewData.value?.data.filter((cast) => cast.castCrew?.name == 'Actor/Actress') || [];
  },
);

watch(
  () => [locationReleases.value, musicReleases.value, talentReleases.value],
  () => {
    let completed = true;

    for (const release of locationReleases.value) {
      if (!release.release) {
        completed = false;
        break;
      }
    }
    for (const release of musicReleases.value) {
      if (!release.release) {
        completed = false;
        break;
      }
    }
    for (const release of talentReleases.value) {
      if (!release.release) {
        completed = false;
        break;
      }
    }
    if (completed) {
      releaseStore.markDone.mutateAsync(submissionId.value);
    } else {
      releaseStore.markNotDone.mutateAsync(submissionId.value);
    }
    emit('releaseStatus', completed);
  },
  { deep: true },
);
</script>
